/**
 * ProducerFoodFika – public-facing "Mat & fika" section on the producer detail page.
 * Renders only when the producer has meaningful food_description or food_serving_types.
 * Follows Farmly editorial tone: calm, place-first, not transactional.
 */

import { Badge } from '@/components/ui/badge';
import { RichTextRenderer } from '@/components/common/RichTextRenderer';
import { SERVING_TYPES } from '@/lib/producer/foodFika';
import { useTranslate } from '@/i18n';

interface ProducerFoodFikaProps {
  foodDescription: string | null | undefined;
  foodServingTypes: string[] | null | undefined;
}

export function ProducerFoodFika({ foodDescription, foodServingTypes }: ProducerFoodFikaProps) {
  const t = useTranslate();
  const types = foodServingTypes ?? [];
  const hasDescription = !!foodDescription?.trim();
  const hasTypes = types.length > 0;

  // Hide if no meaningful content
  if (!hasDescription && !hasTypes) return null;

  return (
    <section>
      <div className="max-w-2xl">
        <h2 className="font-display text-lg text-foreground mb-3">{t("producer.food.heading")}</h2>

        {/* Serving type chips */}
        {hasTypes && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {types.map((key) => {
              const label = SERVING_TYPES.find((s) => s.key === key)?.label ?? key;
              return (
                <Badge key={key} variant="outline" className="text-xs font-normal">
                  {label}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Rich text description */}
        {hasDescription && (
          <div className="farmly-richtext">
            <RichTextRenderer html={foodDescription!} />
          </div>
        )}
      </div>
    </section>
  );
}
